import { useGetAgendaRecordQuery, useGetAgendaRecordsCreateQuery } from 'store/services/flow/agendaRecords';
import { useEffect, useMemo, useRef } from 'react';
import type { GetAgendaRecordRes, RecordFormItem } from 'types';

type RecordTabs = GetAgendaRecordRes['tabs'];

export const useAgendaRecordData = (agendaId: string, isNew: boolean, recordId: string = '') => {
  const { currentData: createData, isFetching: isCreating } = useGetAgendaRecordsCreateQuery({ agendaId }, { skip: !isNew });
  const { currentData: recordData, isFetching: isRecordFetching } = useGetAgendaRecordQuery({ agendaId, recordId }, { skip: isNew || !recordId });

  const sourceTabs = isNew ? createData?.tabs : recordData?.tabs;
  const isFetching = isCreating || isRecordFetching;

  const lastLoadedRef = useRef<{ key: string; tabs: RecordTabs }>();

  useEffect(() => {
    if (sourceTabs !== undefined) {
      lastLoadedRef.current = { key: agendaId, tabs: sourceTabs };
    }
  }, [sourceTabs, agendaId]);

  const { tabs, items } = useMemo(() => {
    const cache = lastLoadedRef.current;
    const fallbackTabs = isFetching && cache?.key === agendaId ? cache.tabs : [];
    const processedTabs = sourceTabs !== undefined ? sourceTabs : fallbackTabs;

    const processedItems =
      processedTabs.length === 0
        ? []
        : processedTabs.reduce<RecordFormItem[]>((acc, tab) => {
            const sectionItems = tab.sections.reduce<RecordFormItem[]>((sectionAcc, section) => {
              return sectionAcc.concat(section.formItems);
            }, []);
            return acc.concat(sectionItems);
          }, []);

    return { tabs: processedTabs, items: processedItems };
  }, [sourceTabs, isFetching, agendaId]);

  const record = recordData?.record;

  return {
    tabs,
    record,
    items,
    isFetching,
  };
};
